import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import StatusBadge from '../../components/shared/StatusBadge';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { ChevronRight, Copy, MessageCircle, Wheat } from 'lucide-react';

export default function HomeCliente() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [perfil, setPerfil] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/clientes/me'),
      api.get('/pedidos?per_page=8'),
      api.get('/empresa').catch(() => ({ data: {} })),
    ]).then(([perfilRes, pedidosRes, empresaRes]) => {
      setPerfil(perfilRes.data);
      setPedidos(pedidosRes.data.pedidos || []);
      setEmpresa(empresaRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const hasActivePedido = pedidos.some(p =>
    ['Recebido', 'Em Produção', 'Pronto', 'Em Entrega'].includes(p.status)
  );

  const nomeDisplay = perfil?.nome_empresa || perfil?.nome_contato || user?.nome || '';
  const nomeCliente = (perfil?.nome_contato || user?.nome || '').split(' ')[0];
  const nomeEmpresaSistema = empresa?.nome_fantasia || empresa?.nome || empresa?.razao_social || '';
  const logoUrl = empresa?.logo_url || '';

  const handleWhatsApp = () => {
    let numero = empresa?.whatsapp?.replace(/\D/g, '') || '';
    if (!numero) { alert('Número de WhatsApp da empresa não cadastrado.'); return; }
    if (numero.startsWith('55') && numero.length > 11) numero = numero.slice(2);
    const msg = encodeURIComponent(`Olá, sou ${nomeDisplay} e preciso de ajuda com meu pedido.`);
    window.open(`https://wa.me/+55${numero}?text=${msg}`, '_blank');
  };

  const handleDuplicar = (pedidoId) => {
    navigate(`/pedidos?base=${pedidoId}`);
  };

  const handleUltimoPedido = () => {
    if (pedidos.length > 0) navigate(`/pedidos?base=${pedidos[0].id}`);
    else navigate('/pedidos');
  };

  return (
    <div className="pb-4 bg-brand-bg min-h-screen" data-testid="home-cliente">

      {/* ── HEADER BRANDED ── */}
      <div className="bg-brand-primary pt-10 pb-0 shadow-md">
        {/* Linha com logo + empresa + saudação */}
        <div className="px-5 pb-4 flex items-center gap-3">
          {/* Logo da empresa */}
          <div className="w-12 h-12 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img
              src={logoUrl || '/logo-fatima.jpg'}
              alt={nomeEmpresaSistema}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Nome da empresa + saudação */}
          <div className="flex-1 min-w-0">
            {nomeEmpresaSistema && (
              <p className="text-white font-heading font-bold text-base leading-tight truncate">
                {nomeEmpresaSistema}
              </p>
            )}
            <p className="text-white/80 font-body text-sm">
              {nomeCliente ? `Olá, ${nomeCliente}!` : 'Bem-vindo!'}
            </p>
          </div>
        </div>

        {/* Faixa info do cliente — fundo levemente mais escuro */}
        <div className="bg-brand-dark/20 px-5 py-3">
          {perfil?.turno && (
            <p className="text-sm font-body text-white/90">
              Turno de entrega: <span className="font-semibold text-white">{perfil.turno}</span>
            </p>
          )}
          {perfil?.endereco_formatado && (
            <p className="text-sm font-body text-white/70 mt-0.5 truncate">
              {perfil.endereco_formatado}
            </p>
          )}
          {/* Status indicator */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs font-body text-white/70">Pedido ativo:</span>
            <span
              className={`w-3 h-3 rounded-full inline-block ${hasActivePedido ? 'bg-green-400' : 'bg-white/30'}`}
              title={hasActivePedido ? 'Pedido em andamento' : 'Nenhum pedido ativo'}
              data-testid="status-indicator"
            />
            <span className="text-xs font-body text-white/70">
              {hasActivePedido ? 'Sim' : 'Não'}
            </span>
          </div>
        </div>
      </div>

      {/* Quick action buttons */}
      <div className="px-4 pt-5 pb-4 flex gap-2">
        <button
          onClick={() => navigate('/pedidos')}
          className="flex-1 py-3 rounded-2xl bg-brand-accent-mid text-brand-dark font-dashboard font-semibold text-sm hover:bg-brand-accent-mid/80 transition-colors"
          data-testid="btn-novo-pedido"
        >
          Novo Pedido
        </button>
        <button
          onClick={handleUltimoPedido}
          className="flex-1 py-3 rounded-2xl bg-brand-accent-mid text-brand-dark font-dashboard font-semibold text-sm hover:bg-brand-accent-mid/80 transition-colors"
          data-testid="btn-ultimo-pedido"
        >
          Último Pedido
        </button>
        <button
          onClick={handleWhatsApp}
          className="flex-1 py-3 rounded-2xl bg-brand-accent-mid text-brand-dark font-dashboard font-semibold text-sm hover:bg-brand-accent-mid/80 transition-colors flex items-center justify-center gap-1.5"
          data-testid="btn-whatsapp"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="text-xs">Suporte</span>
        </button>
      </div>

      {/* Histórico de Pedidos section */}
      <div className="mx-4 rounded-2xl overflow-hidden shadow-sm">
        <div className="bg-brand-accent-mid px-4 py-3">
          <p className="text-center font-dashboard font-bold text-brand-dark text-sm">Histórico de Pedidos</p>
        </div>

        <div className="bg-white">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : pedidos.length === 0 ? (
            <div className="text-center py-10 px-4">
              <p className="text-brand-text-muted font-body text-sm">Você ainda não fez nenhum pedido.</p>
              <button
                onClick={() => navigate('/pedidos')}
                className="mt-3 text-brand-primary font-dashboard font-semibold text-sm hover:underline"
              >
                Fazer primeiro pedido
              </button>
            </div>
          ) : (
            <div>
              {pedidos.map((p, i) => (
                <div
                  key={p.id}
                  className={`px-4 py-3.5 ${i < pedidos.length - 1 ? 'border-b border-brand-border' : ''}`}
                  data-testid="pedido-card"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/pedidos/${p.id}`)}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-dashboard font-bold text-brand-text text-sm">#{p.numero}</span>
                        <StatusBadge status={p.status} />
                      </div>
                      <p className="text-xs text-brand-text-muted font-body">
                        {formatDate(p.data_pedido)} · {p.itens?.length || 0} item(ns) · Prazo: {p.prazo}
                      </p>
                      {p.total != null && (
                        <p className="text-xs font-semibold text-brand-primary mt-0.5">{formatCurrency(p.total)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 ml-3 flex-shrink-0">
                      <button
                        onClick={() => handleDuplicar(p.id)}
                        className="text-brand-text-muted hover:text-brand-primary transition-colors"
                        title="Duplicar pedido"
                        data-testid="duplicar-pedido-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/pedidos/${p.id}`)}
                        className="text-brand-text-muted hover:text-brand-primary transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {pedidos.length > 0 && (
        <button
          onClick={() => navigate('/pedidos/historico')}
          className="w-full mt-3 text-center text-sm text-brand-primary font-dashboard font-semibold py-2"
        >
          Ver todo o histórico
        </button>
      )}
    </div>
  );
}
